// MultiStepForm.js
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Container,
  Form,
  Button,
  ProgressBar,
  Nav,
  Alert,
} from "react-bootstrap";
import SignaturePad from "react-signature-canvas"; // Import the SignaturePad component
import "./Signature.css";
import Add from "@mui/icons-material/Add";
import { connect } from "react-redux";
import { getSasToken, setCurrentDocument } from "../../store/thunk/user";
import {
  handleDocumentUploads,
  uploadDocumentAndGetUrl,
} from "../../utils/uploadDocument";
import { dataURLtoBlob } from "../../utils/dataURLtoBlob";
import { updateForm } from "../../utils/updateForm";
import { toast } from "react-toastify";
import { triggerButtonClick } from "../../utils/triggerLibraryButton";
import ComplianceAndOtherDocumentsDownload from "./Download/ComplianceAndOtherDocumentsDownload";
import ComplianceDownload from "../common/ComplianceDownload/ComplianceDownload";
import { showLoader, hideLoader }  from "js-loader-fn";

const ComplianceAndOtherDocuments = ({
  currentDocument,
  documentList,
  user,
  sasToken,
  ipAddress,
  getSasToken,
}) => {
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(0); // Progress percentage for each step
  const [formData, setFormData] = useState({
    uploadedDocuments: null,
    passportOrBirthCertificate: null,
    proofOfNationalInsurance: null,
    headAndShoulderPhoto: null,
    idOrBiometricFront: null,
    idOrBiometricBack: null,
    additionalCertificate: null,
    bankDetails: null,
    proofOfAddress: null,
    universityEnrollmentLetter: null,
    universityTermVacationDate: null,
    documentSignature: "",
    shareCode: "",
    consent: false,
    signature: "", // Store the signature data as a string
  });

  const [errors, setErrors] = useState({});
  const sigPadRef = useRef(null); // Reference to the SignaturePad component
  const documentSigPadRef = useRef(null); // Reference to the SignaturePad component
  useEffect(() => {
    setFormData(currentDocument);
    if (currentDocument?.step) {
      setStep(currentDocument?.step);
    }
    if (currentDocument?.progress) {
      setProgress(currentDocument?.progress);
    }
    if (currentDocument?.progress === 100) {
      getSasToken();
    }
  }, [currentDocument]);
  // Function to handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: files[0], // Store the selected file
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null })); // Clear error on file change
  };
  // Function to handle file removal
  const handleFileRemove = (name) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: null,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
  };

  // Function to validate each step
  const validateStep = () => {
    let formErrors = {};
    if (step === 1) {
      if (documentSigPadRef.current.isEmpty()) {
        formErrors.documentSignature = "This field is required"; // Check if the signature pad is empty
      }
      if (!formData.proofOfNationalInsurance) {
        formErrors.proofOfNationalInsurance =
          "Please upload the proof of national insurance.";
      }
      if (!formData.passportOrBirthCertificate) {
        formErrors.passportOrBirthCertificate =
          "Please upload passport or birth certificate.";
      }
      if (!formData.headAndShoulderPhoto) {
        formErrors.headAndShoulderPhoto = "Please upload the photo.";
      }
      if (!formData.bankDetails) {
        formErrors.bankDetails = "Please upload proof of bank details.";
      }
    } else if (step === 2) {
      if (!formData.consent) formErrors.consent = "You must agree to the terms";
      if (sigPadRef.current.isEmpty())
        formErrors.signature = "Signature is required"; // Check if the signature pad is empty
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Function to go to the next step
  const nextStep = async () => {
    if (validateStep()) {
      showLoader({title: "Please wait..."});
      delete formData?.uploadedDocuments;
      const updatedDocuments = await handleDocumentUploads(
        formData,
        user?.token
      );
      if (updatedDocuments) {
        const documentSignatureImage = documentSigPadRef.current
          .getTrimmedCanvas()
          .toDataURL("image/png");
        const signatureBlob = dataURLtoBlob(documentSignatureImage);
        // Create a FormData object
        const formDataSign = new FormData();
        // Append the Blob as a file to the FormData
        // The second argument is the file name, which you can customize as needed
        formDataSign.append(
          "file",
          signatureBlob,
          `${[currentDocument["key"]]}.png`
        );
        // Send the FormData using postMultiPartFormData
        const documentUrl =
          (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";

        if (documentUrl) {
          const payload = {
            [currentDocument["key"]]: {
              name: currentDocument?.name,
              ...formData,
              uploadedDocuments: { ...updatedDocuments },
              documentSignature: documentUrl,
              progress: progress + 50,
              step: step + 1,
            },
          };
          setFormData({
            ...formData,
            uploadedDocuments: updatedDocuments,
          });
          updateForm(payload, documentList, user);
          setStep(step + 1);
          setProgress(progress + 50);
        } else {
          toast.error(
            "Something went wrong while upload signature. please try again."
          );
        }
        hideLoader();
      } else {
        hideLoader();
        // toast.error(
        //   "Something went wrong while uploading some documents. please try again."
        // );
      }
    }
  };

  // Function to go to the previous step
  const prevStep = () => {
    setStep(step - 1);
    setProgress(progress - 50);
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      // Get the signature data as a base64 image
      const signatureImage = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setFormData({
        ...formData,
        signature: signatureImage,
      });
      const signatureBlob = dataURLtoBlob(signatureImage);

      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob, 
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            progress: 100,
            step: 2,
            signature: documentUrl,
            signDate: new Date(),
            ipAddress: ipAddress,
          },
        };
        updateForm(payload, documentList, user);
        toast.success("Successfully filled the form.");
        triggerButtonClick();
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
      // Additional submit logic here, such as sending formData to the server
    }
  };
  // Clear the signature pad
  const clearSignature = () => {
    sigPadRef.current.clear();
    setFormData({ ...formData, signature: "" });
  };
  // Clear the signature pad
  const clearDocumentSignature = () => {
    documentSigPadRef.current.clear();
    setFormData({ ...formData, documentSignature: "" });
  };
  return (
    <Container className="mt-5">
      {currentDocument?.progress === 100 ? (
        <>
          <ComplianceAndOtherDocumentsDownload
            data={currentDocument}
            sasToken={sasToken}
            user={documentList?.userDetail}
          />
          <ComplianceDownload
            sasToken={sasToken}
            documents={currentDocument?.uploadedDocuments}
          />
          <ul className="list-group">
            <li class="list-group-item d-flex justify-content-between">
              Sharecode <span>{currentDocument?.shareCode || "--"}</span>
            </li>

            <li className="list-group-item d-flex justify-content-between align-items-center">
              <div className="row w-100">
                {/* Text column */}
                <div className="col-6 d-flex align-items-center">
                  I certify that all documents and scans uploaded belong to
                  myself and are a true reflection of my identity.
                </div>

                {/* Image column */}
                <div className="col-6 text-end">
                  {currentDocument?.signature && (
                    <img
                      width={100}
                      src={`${currentDocument?.signature}?${sasToken}`}
                      alt="Signature"
                    />
                  )}
                </div>
              </div>
            </li>
          </ul>
        </>
      ) : (
        <>
          <Nav variant="pills" className="justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link
                className={step === 1 ? "text-light" : ""}
                active={step === 1}
              >
                Step 1
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 2 ? "text-light" : ""}
                active={step === 2}
              >
                Step 2
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <ProgressBar now={currentDocument?.progress || 0} className="mb-4" />

          <Form onSubmit={handleSubmit}>
            {step === 1 && (
              <div>
                <h3>Section 1: Passport or Birth Certificate</h3>
                <Form.Group className="mb-3">
                  <Form.Label>Passport or Birth Certificate</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.passportOrBirthCertificate ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.passportOrBirthCertificate.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("passportOrBirthCertificate")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="passportOrBirthCertificate"
                          name="passportOrBirthCertificate"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("passportOrBirthCertificate")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                  {errors.passportOrBirthCertificate && (
                    <Alert variant="danger">
                      {errors.passportOrBirthCertificate}
                    </Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Proof of National Insurance</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.proofOfNationalInsurance ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.proofOfNationalInsurance.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("proofOfNationalInsurance")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="proofOfNationalInsurance"
                          name="proofOfNationalInsurance"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("proofOfNationalInsurance")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                  {errors.proofOfNationalInsurance && (
                    <Alert variant="danger">
                      {errors.proofOfNationalInsurance}
                    </Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Head and shoulders photo (To compare with Passport)
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.headAndShoulderPhoto ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.headAndShoulderPhoto.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("headAndShoulderPhoto")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="headAndShoulderPhoto"
                          name="headAndShoulderPhoto"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("headAndShoulderPhoto")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                  {errors.headAndShoulderPhoto && (
                    <Alert variant="danger">
                      {errors.headAndShoulderPhoto}
                    </Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>EU ID Card or Biometric Card Front</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.idOrBiometricFront ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.idOrBiometricFront.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() => handleFileRemove("idOrBiometricFront")}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="idOrBiometricFront"
                          name="idOrBiometricFront"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("idOrBiometricFront")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>EU ID Card or Biometric Card Back</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.idOrBiometricBack ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.idOrBiometricBack.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() => handleFileRemove("idOrBiometricBack")}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="idOrBiometricBack"
                          name="idOrBiometricBack"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document.getElementById("idOrBiometricBack").click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Additional Certs or Tickets (FLT, VNA, Reach Etc.)
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.additionalCertificate ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.additionalCertificate.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("additionalCertificate")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="additionalCertificate"
                          name="additionalCertificate"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("additionalCertificate")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Proof of Bank Details (Bank card or statement)
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.bankDetails ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.bankDetails.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() => handleFileRemove("bankDetails")}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="bankDetails"
                          name="bankDetails"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document.getElementById("bankDetails").click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                  {errors.bankDetails && (
                    <Alert variant="danger">{errors.bankDetails}</Alert>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Proof of Address</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.proofOfAddress ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.proofOfAddress.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() => handleFileRemove("proofOfAddress")}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="proofOfAddress"
                          name="proofOfAddress"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document.getElementById("proofOfAddress").click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Upload University Enrollment Letter </Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.universityEnrollmentLetter ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.universityEnrollmentLetter.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("universityEnrollmentLetter")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="universityEnrollmentLetter"
                          name="universityEnrollmentLetter"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("universityEnrollmentLetter")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>University Term/Vacation Dates</Form.Label>
                  <div className="d-flex align-items-center">
                    {formData.universityTermVacationDate ? (
                      <div className="d-flex flex-column w-100">
                        <span className="text-success">
                          {formData.universityTermVacationDate.name}
                        </span>
                        <Button
                          variant="danger"
                          className="mt-2"
                          onClick={() =>
                            handleFileRemove("universityTermVacationDate")
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          id="universityTermVacationDate"
                          name="universityTermVacationDate"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          className="w-100 bg-success text-white"
                          onClick={() =>
                            document
                              .getElementById("universityTermVacationDate")
                              .click()
                          }
                        >
                          <Add /> Select File
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Share Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="shareCode"
                    value={formData.shareCode}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    I certify that all documents and scans uploaded belong to
                    myself and are a true reflection of my identity.
                  </Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={documentSigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.documentSignature && (
                    <Alert variant="danger">{errors.documentSignature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearDocumentSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Step 2: Sign and Submit</h3>
                <p>
                  By signing this page, I confirm that I fully understand the
                  content and agree to adhere to it at all times. I also agree
                  that all the details provided in this document are accurate
                  and complete to the best of my knowledge.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Signature</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={sigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.signature && (
                    <Alert variant="danger">{errors.signature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    label="By ticking this box I consent to the company processing my data and the evidence of this document being stored within their system for the purpose of work finding services."
                    checked={formData.consent} // Use checked for checkboxes
                    onChange={handleChange}
                    isInvalid={!!errors.consent}
                  />
                  {errors.consent && (
                    <Alert variant="danger">{errors.consent}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="success" type="submit">
                  Submit
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currentDocument: state.user.currentDocument,
  documentList: state.user.documentList,
  user: state.user.loggedInUserData,
  sasToken: state.user.sasToken,
  ipAddress: state.user.ipAddress,
});
export default connect(mapStateToProps, { getSasToken, setCurrentDocument })(
  ComplianceAndOtherDocuments
);
